import React, { useState, useRef, useEffect } from "react";
import { connect } from "react-redux";
import { API_ROOT } from "../../../baseUrls";
import { updateMindfilePicture } from "../../../state/actions/updateDataActions";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  IconButton,
} from "@material-ui/core";
import CloseIcon from "@mui/icons-material/Close";

function AvatarDialog({
  isOpen,
  closeDialog,
  mindfile,
  mindfileId,
  updatingAvatar,
  updateMindfilePicture,
}) {
  const [file, setFile] = useState(null);
  // This is the number of pending avatar GETs, usually 0 or 1.
  const [, setAvatarsFetching] = useState(0);
  // This boolean indicates the user cannot access the avatar at the moment. Accessing the client-side
  // image while the client syncs with the server just leads to confusing situations.
  const [avatarLocked, setAvatarLocked] = useState(false);
  const preview = useRef(null);


  // Load avatar.
  useEffect(() => {
    if (!mindfileId) return;
    // The avatar is locked when updating starts or when the mindfile changes.
    setAvatarLocked(true);
    // The code after this (fetching the new image) will run when updating ends.
    if (updatingAvatar) return;
    setAvatarsFetching(n => n + 1);
    const imgUrl = `${API_ROOT}/mindfiles/${mindfileId}/avatar/thumbnail?size=256&ts=${mindfile.timestamp}`;
    fetch(imgUrl, {
      method: "GET",
      credentials: "include",
    })
      .then(resp => resp.blob())
      .then(imgBlob => {
        const file = new File([imgBlob], "avatar.jpg", { type: "image/jpeg" });
        setFile(file);
      })
      .finally(() => {
        setAvatarsFetching(avatarsFetching => {
          if (avatarsFetching === 1) {
            // The avatar is unlocked when all the GETs are finished.
            setAvatarLocked(false);
          }
          return avatarsFetching - 1;
        });
      });
  }, [mindfile.timestamp, mindfileId, updatingAvatar]);

  // Process file and draw to canvas.
  useEffect(() => {
    if (isOpen) {
      if (avatarLocked) {
        // Prevent the user from seeing the now out-of-date avatar during syncing.
        clearPreview();
      } else if (file) {
        processfile();
      }
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file, avatarLocked, isOpen]);

  const updateImage = e => {
    const file = e.target.files.item(0);
    setFile(file);
  };

  const uploadFile = () => {
    updateMindfilePicture(file);
    closeDialog();
  };

  const clearPreview = () => {
    if (!preview.current) return;

    const canvas = preview.current;
    const ctx = preview.current.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const processfile = () => {
    if (!/image/i.test(file.type)) {
      alert("File " + file.name + " is not an image.");
      return false;
    }

    // Read the file.
    var reader = new FileReader();
    reader.readAsArrayBuffer(file);

    reader.onload = function(event) {
      // Transform file to blob url.
      var blob = new Blob([event.target.result]);
      window.URL = window.URL || window.webkitURL;
      var blobUrl = window.URL.createObjectURL(blob);

      // Load blob to image object.
      var image = new Image();
      image.src = blobUrl;
      image.onload = function() {
        resizeMe(image);
      };
    };
  };

  const resizeMe = img => {
    if (!preview.current) return;

    let width = img.width;
    let height = img.height;
    const min = Math.min(width, height);
    const max = Math.max(width, height);
    const offset = (max - min) / 2;

    let offsetX = 0;
    let offsetY = 0;
    if (width > height) {
      offsetX = offset;
    } else {
      offsetY = offset;
    }

    // Resize the canvas and draw the image data into it.
    preview.current.width = 256;
    preview.current.height = 256;
    var ctx = preview.current.getContext("2d");
    ctx.drawImage(img, offsetX, offsetY, min, min, 0, 0, 256, 256);

    return preview.current.toDataURL("image/png"); // get the data from canvas as PNG
  };

  return (
    <Dialog onClose={closeDialog} open={isOpen}>
      <DialogTitle onClose={closeDialog}>
        Avatar
        <IconButton
          aria-label="close"
          style={{ position: "absolute", right: 8, top: 8 }}
          onClick={closeDialog}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column">
          <Box>
            <canvas ref={preview} />
          </Box>
          <input type="file" onChange={updateImage} disabled={avatarLocked} />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button color="primary" variant="contained" onClick={uploadFile} disabled={avatarLocked}>
          Update
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const mapStateToProps = state => ({
  mindfile: state.mindfile,
  mindfileId: state.mindfile.id,
  updatingAvatar: state.mindfile.picturesUploading > 0,
});

const mapDispatchToProps = {
  updateMindfilePicture,
};

export default connect(mapStateToProps, mapDispatchToProps)(AvatarDialog);
